import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ModalMainStyled } from '../modal/modal.styled';
import { useObserver } from 'mobx-react-lite';
import tagsStore from '../../../stores/tags.store';
import { COLORS } from '../../styleComponents/Colors';
import { addToast } from '../toasts/Toast';
import {
    EuiButton,
    EuiFieldText,
    EuiFormRow,
    EuiIcon,
    EuiModal,
    EuiModalBody,
    EuiModalFooter,
    EuiModalHeader,
    EuiModalHeaderTitle
} from '@elastic/eui';

const ModalContentStyled = styled.div`
  color: rgba(255, 255, 255, 0.85);

  .euiFormRow {
    margin-bottom: 0;
  }
`;

const ModalHeaderContentStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .title-icon {
    align-items: center;
    background: rgba(0, 128, 213, 0.12);
    border: 1px solid rgba(0, 128, 213, 0.25);
    border-radius: 6px;
    color: #36A2EF;
    display: flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;
  }

  h1 {
    font-size: 24px;
    line-height: 1.2;
    margin: 0;
  }

  p {
    color: rgba(255, 255, 255, 0.55);
    font-size: 13px;
    line-height: 1.45;
    margin: 6px 0 0;
  }
`;

const BodyGridStyled = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.85fr);
  gap: 16px;
  min-height: 430px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const PanelStyled = styled.section`
  background: linear-gradient(135deg, rgb(35, 37, 47) 0%, rgb(24, 25, 30) 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  min-width: 0;
  overflow: hidden;
`;

const PanelHeaderStyled = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;

  h3 {
    font-size: 15px;
    line-height: 1.25;
    margin: 0;
  }

  p {
    color: rgba(255, 255, 255, 0.52);
    font-size: 12px;
    line-height: 1.4;
    margin: 5px 0 0;
  }

  .count-pill {
    background: rgba(0, 128, 213, 0.14);
    border: 1px solid rgba(0, 128, 213, 0.24);
    border-radius: 999px;
    color: #36A2EF;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
  }
`;

const TagsGridStyled = styled.div`
  align-content: start;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-height: 338px;
  overflow: auto;
  padding: 16px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const TagButtonStyled = styled.button`
  align-items: center;
  background: ${ props => props.selected ? 'rgba(0, 128, 213, 0.12)' : 'rgba(255, 255, 255, 0.03)' };
  border: 1px solid ${ props => props.selected ? 'rgba(0, 128, 213, 0.42)' : 'rgba(255, 255, 255, 0.06)' };
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 9px;
  min-height: 42px;
  padding: 9px 10px;
  text-align: left;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.055);
    border-color: rgba(0, 128, 213, 0.28);
    transform: translateY(-1px);
  }

  .swatch {
    border-radius: 50%;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.08);
    display: block;
    height: 14px;
    width: 14px;
  }

  .tag-name {
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tag-id {
    color: rgba(255, 255, 255, 0.38);
    display: block;
    font-family: Consolas, Monaco, monospace;
    font-size: 10px;
    font-weight: 400;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EmptyStateStyled = styled.div`
  align-content: center;
  color: rgba(255, 255, 255, 0.54);
  display: grid;
  font-size: 13px;
  min-height: 220px;
  padding: 24px;
  text-align: center;
`;

const FormStackStyled = styled.div`
  display: grid;
  gap: 14px;
  padding: 16px;
`;

const FormPanelStyled = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 14px;

  h4 {
    font-size: 14px;
    margin: 0 0 4px;
  }

  .form-help {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    line-height: 1.4;
    margin: 0 0 14px;
  }
`;

const ColorGridStyled = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(5, 1fr);
  margin-top: 8px;

  @media (max-width: 520px) {
    grid-template-columns: repeat(5, minmax(34px, 1fr));
  }
`;

const ColorButtonStyled = styled.button`
  background: ${ props => props.color };
  border: 2px solid ${ props => props.selected ? '#fff' : 'rgba(255, 255, 255, 0.12)' };
  border-radius: 6px;
  box-shadow: ${ props => props.selected ? '0 0 0 2px rgba(0, 128, 213, 0.5)' : 'none' };
  cursor: pointer;
  height: 32px;
  min-width: 32px;
`;

const ActionRowStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const SelectedSummaryStyled = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px;

  .swatch {
    border-radius: 50%;
    display: block;
    height: 18px;
    width: 18px;
  }

  .name {
    color: rgba(255, 255, 255, 0.92);
    display: block;
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FooterHintStyled = styled.div`
  color: rgba(255, 255, 255, 0.44);
  font-size: 12px;
  line-height: 1.4;
`;

const emptyTag = { name: '', color: '1' };

const ColorPicker = ({ selectedId, selectEvent }) => {
    return (
        <ColorGridStyled>
            { Object.keys(COLORS).map(id => (
                <ColorButtonStyled
                    aria-label={ `Select color ${ COLORS[id] }` }
                    color={ COLORS[id] }
                    key={ id }
                    onClick={ () => selectEvent(id) }
                    selected={ selectedId === id }
                    type="button"
                />
            )) }
        </ColorGridStyled>
    );
};

const TagButton = ({ tag, selected, selectEvent }) => {
    return (
        <TagButtonStyled
            onClick={ () => selectEvent(tag._id, !selected) }
            selected={ selected }
            type="button"
        >
            <span className="swatch" style={ { background: COLORS[tag.color] } }/>
            <span>
                <span className="tag-name">{ tag.name }</span>
                <span className="tag-id">{ tag._id }</span>
            </span>
            { selected && <EuiIcon type="check" size="s"/> }
        </TagButtonStyled>
    );
};

const Observed = ({ tags, close }) => {
    const [newTag, setNewTag] = useState(emptyTag);
    const [editTag, seEditTag] = useState({ name: '', color: '1' });
    const [selectEditTag, setSelectEditTag] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const tagsExist = !!(tags && tags.length);
    const selectedTag = useMemo(() => {
        if (!selectEditTag || !tags) {
            return null;
        }
        return tags.find(item => item._id === selectEditTag);
    }, [selectEditTag, tags]);

    const addNewTag = async () => {
        if (!newTag.name || !newTag.color || isSaving) {
            return;
        }

        setIsSaving(true);
        try {
            await tagsStore.createTag(newTag);
            addToast('Tag created', 'suc');
            setNewTag(emptyTag);
        } catch (error) {
            addToast(error.message || 'Failed to create tag', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const editTagHandle = async () => {
        if (!editTag.name || !editTag.color || !selectEditTag || isSaving) {
            return;
        }

        setIsSaving(true);
        try {
            await tagsStore.editTag(selectEditTag, editTag);
            addToast('Tag updated', 'suc');
            seEditTag({ name: '', color: '1' });
            setSelectEditTag(null);
            setDeleteConfirm(false);
        } catch (error) {
            addToast(error.message || 'Failed to update tag', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const deleteTagHandle = async () => {
        if (!selectEditTag || isSaving) {
            return;
        }

        if (!deleteConfirm) {
            setDeleteConfirm(true);
            return;
        }

        setIsSaving(true);
        try {
            await tagsStore.deleteTag(selectEditTag);
            addToast('Tag deleted', 'suc');
            seEditTag({ name: '', color: '1' });
            setSelectEditTag(null);
            setDeleteConfirm(false);
        } catch (error) {
            addToast(error.message || 'Failed to delete tag', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const selectEvent = (id, isSelected) => {
        if (isSelected) {
            const nextSelectedTag = tags.find(d => d._id === id);
            setSelectEditTag(id);
            seEditTag({ name: nextSelectedTag.name, color: nextSelectedTag.color });
            setDeleteConfirm(false);
        } else {
            setSelectEditTag(null);
            seEditTag({ name: '', color: '1' });
            setDeleteConfirm(false);
        }
    };

    const setLimitedTagName = (value, setter, tagData) => {
        if (value.length < 15) {
            setter({ ...tagData, name: value });
        }
    };

    return (
        <ModalMainStyled>
            <EuiModal onClose={ close } style={ { width: 'min(920px, calc(100vw - 24px))' } }>
                <EuiModalHeader>
                    <EuiModalHeaderTitle>
                        <ModalHeaderContentStyled>
                            <span className="title-icon">
                                <EuiIcon type="tag" size="m"/>
                            </span>
                            <div>
                                <h1>Tags management</h1>
                                <p>Create labels, update colors, and keep monitor grouping tidy.</p>
                            </div>
                        </ModalHeaderContentStyled>
                    </EuiModalHeaderTitle>
                </EuiModalHeader>

                <EuiModalBody>
                    <ModalContentStyled>
                        <BodyGridStyled>
                            <PanelStyled>
                                <PanelHeaderStyled>
                                    <div>
                                        <h3>Existing tags</h3>
                                        <p>Select a tag to edit its name or color.</p>
                                    </div>
                                    <span className="count-pill">{ tags ? tags.length : 0 } total</span>
                                </PanelHeaderStyled>

                                { tagsExist && (
                                    <TagsGridStyled>
                                        { tags.map((item, idx) => {
                                            const isSelected = selectEditTag === item._id;
                                            return (
                                                <TagButton
                                                    selected={ isSelected }
                                                    selectEvent={ selectEvent }
                                                    key={ `${ item._id }_dzjjjawd-tag_${ idx }` }
                                                    tag={ item }
                                                />);
                                        }) }
                                    </TagsGridStyled>
                                ) }

                                { !tagsExist && (
                                    <EmptyStateStyled>
                                        No tags yet. Create the first one and it will be available for monitors.
                                    </EmptyStateStyled>
                                ) }
                            </PanelStyled>

                            <PanelStyled>
                                <PanelHeaderStyled>
                                    <div>
                                        <h3>{ selectedTag ? 'Edit selected tag' : 'Create tag' }</h3>
                                        <p>{ selectedTag ? 'Changes apply anywhere this tag is used.' : 'New tags can be assigned from monitor edit pages.' }</p>
                                    </div>
                                </PanelHeaderStyled>

                                <FormStackStyled>
                                    <FormPanelStyled>
                                        <h4>Create new tag</h4>
                                        <p className="form-help">Names can be up to 14 characters.</p>
                                        <EuiFormRow label="Tag name">
                                            <EuiFieldText
                                                fullWidth={ true }
                                                placeholder="prod"
                                                value={ newTag.name }
                                                onChange={ e => setLimitedTagName(
                                                    e.target.value,
                                                    setNewTag,
                                                    newTag
                                                ) }
                                            />
                                        </EuiFormRow>
                                        <ColorPicker
                                            selectEvent={ id => setNewTag({ ...newTag, color: id }) }
                                            selectedId={ newTag.color }
                                        />
                                        <ActionRowStyled>
                                            <EuiButton
                                                iconType="plusInCircle"
                                                isDisabled={ !newTag.name }
                                                isLoading={ isSaving && !selectEditTag }
                                                size="s"
                                                onClick={ addNewTag }
                                                fill
                                            >
                                                Add tag
                                            </EuiButton>
                                        </ActionRowStyled>
                                    </FormPanelStyled>

                                    <FormPanelStyled>
                                        <h4>Edit tag</h4>
                                        <p className="form-help">Select a tag from the list to make changes.</p>
                                        { selectedTag && (
                                            <SelectedSummaryStyled>
                                                <span className="swatch" style={ { background: COLORS[editTag.color] } }/>
                                                <span className="name">{ selectedTag.name }</span>
                                            </SelectedSummaryStyled>
                                        ) }
                                        <EuiFormRow label="Tag name">
                                            <EuiFieldText
                                                disabled={ !selectedTag }
                                                fullWidth={ true }
                                                value={ editTag.name }
                                                onChange={ e => setLimitedTagName(
                                                    e.target.value,
                                                    seEditTag,
                                                    editTag
                                                ) }
                                            />
                                        </EuiFormRow>
                                        <ColorPicker
                                            selectEvent={ id => seEditTag({ ...editTag, color: id }) }
                                            selectedId={ editTag.color }
                                        />
                                        <ActionRowStyled>
                                            <EuiButton
                                                iconType="save"
                                                isDisabled={ !selectedTag || !editTag.name }
                                                isLoading={ isSaving && !!selectEditTag && !deleteConfirm }
                                                size="s"
                                                onClick={ editTagHandle }
                                                fill
                                            >
                                                Save changes
                                            </EuiButton>

                                            <EuiButton
                                                iconType={ deleteConfirm ? 'alert' : 'trash' }
                                                isDisabled={ !selectedTag }
                                                isLoading={ isSaving && !!selectEditTag && deleteConfirm }
                                                size="s"
                                                color="danger"
                                                onClick={ deleteTagHandle }
                                                fill={ deleteConfirm }
                                            >
                                                { deleteConfirm ? 'Confirm delete' : 'Delete' }
                                            </EuiButton>

                                            { deleteConfirm && (
                                                <EuiButton
                                                    size="s"
                                                    onClick={ () => setDeleteConfirm(false) }
                                                >
                                                    Cancel
                                                </EuiButton>
                                            ) }
                                        </ActionRowStyled>
                                    </FormPanelStyled>
                                </FormStackStyled>
                            </PanelStyled>
                        </BodyGridStyled>
                    </ModalContentStyled>
                </EuiModalBody>

                <EuiModalFooter>
                    <FooterHintStyled>
                        Tag updates are saved immediately and affect all monitors using that tag.
                    </FooterHintStyled>
                    <EuiButton size="s" color="text" onClick={ close }>
                        Close
                    </EuiButton>
                </EuiModalFooter>
            </EuiModal>
        </ModalMainStyled>
    );
};

const TagsModal = ({ close }) => {
    return useObserver(() => (
        <Observed tags={ tagsStore.tags } close={ close }/>
    ));
};

export default TagsModal;
